// Per-language UI strings for the Mundial 2026 interactive components
// (map, calendar, planner). Kept out of the global i18n bundle because the
// article's own `lang:` frontmatter drives them — an EN article reads in EN
// regardless of the visitor's global UI language. Mirrors the festas `STR`.

import type { Stage } from "./fixtures";
import type { Lang } from "./series";
import type { WatchVenueFeature } from "./venues";

export type MundialStrings = {
  map: {
    heading: string;
    description: string;
    venuesHeading: string;
    locate: string;
    tentative: string;
  };
  calendar: {
    heading: string;
    filterDate: string;
    filterStage: string;
    filterTeam: string;
    onlyPortugal: string;
    all: string;
    tbd: string;
    kickoff: string;
    noResults: string;
    matchesCount: (n: number) => string;
    datesNote: string;
    provisional: string;
  };
  planner: {
    heading: string;
    intro: string;
    add: string;
    added: string;
    whereWatch: string;
    atHome: string;
    pick: string;
    myPlan: string;
    planCount: (n: number) => string;
    exportIcs: string;
    clear: string;
    empty: string;
    savedHint: string;
  };
  stages: Record<Stage, string>;
  features: Record<WatchVenueFeature, string>;
  broadcast: {
    paid: string;
    free: string;
    legend: string;
  };
  result: {
    recentHeading: string;
    finalScore: string;
    afterPens: (h: number, a: number) => string;
    scorers: string;
    ownGoalShort: string;
    penaltyShort: string;
    score: (h: number, a: number) => string;
    none: string;
  };
  standings: {
    heading: string;
    team: string;
    note: string;
    cols: {
      mp: string;
      w: string;
      d: string;
      l: string;
      gf: string;
      ga: string;
      gd: string;
      pts: string;
    };
    colsFull: {
      mp: string;
      w: string;
      d: string;
      l: string;
      gf: string;
      ga: string;
      gd: string;
      pts: string;
    };
  };
  scorers: {
    heading: string;
    player: string;
    goals: string;
  };
  bracket: {
    heading: string;
    intro: string;
  };
};

export const STR: Record<Lang, MundialStrings> = {
  pt: {
    map: {
      heading: "Onde ver o Mundial 2026 em Portugal",
      description:
        "Fan zones, bares e restaurantes com ecrã gigante. Toca num ponto para ver os detalhes.",
      venuesHeading: "Locais",
      locate: "Ver no mapa",
      tentative: "A confirmar",
    },
    calendar: {
      heading: "Calendário do Mundial 2026",
      filterDate: "Dia",
      filterStage: "Fase",
      filterTeam: "Procurar seleção ou grupo",
      onlyPortugal: "Só Portugal",
      all: "Todos",
      tbd: "A confirmar",
      kickoff: "Hora de Portugal",
      noResults: "Sem jogos para estes filtros.",
      matchesCount: (n) => `${n} ${n === 1 ? "jogo" : "jogos"}`,
      datesNote:
        "Datas e horas da fase de grupos confirmadas (hora de Portugal). As eliminatórias são provisórias até ao apuramento.",
      provisional: "Data/hora provisória",
    },
    planner: {
      heading: "Planeie os seus jogos",
      intro:
        "Escolha os jogos que não quer perder e onde os vai ver. O seu plano fica guardado neste dispositivo.",
      add: "Adicionar ao meu plano",
      added: "No meu plano",
      whereWatch: "Onde vai ver?",
      atHome: "Em casa",
      pick: "Escolher local",
      myPlan: "O meu plano",
      planCount: (n) => `${n} ${n === 1 ? "jogo marcado" : "jogos marcados"}`,
      exportIcs: "Exportar para o calendário (.ics)",
      clear: "Limpar plano",
      empty: "Ainda não marcou jogos. Adicione os jogos que quer ver acima.",
      savedHint: "Guardado automaticamente neste navegador.",
    },
    stages: {
      group: "Fase de grupos",
      r32: "16 avos de final",
      r16: "Oitavos de final",
      qf: "Quartos de final",
      sf: "Meias-finais",
      third: "3.º e 4.º lugar",
      final: "Final",
    },
    features: {
      telao: "Ecrã gigante",
      "ar-livre": "Ao ar livre",
      gratis: "Entrada gratuita",
      reservas: "Aceita reservas",
    },
    broadcast: {
      paid: "Canal por subscrição",
      free: "Sinal aberto (grátis)",
      legend:
        "Todos os jogos na Sport TV (por subscrição). Em sinal aberto e grátis: RTP1, SIC ou TVI nos jogos assinalados, e LiveModeTV (YouTube) nos jogos de Portugal, meias-finais e final.",
    },
    result: {
      recentHeading: "Resultados",
      finalScore: "Resultado final",
      afterPens: (h, a) => `nos penáltis ${h}–${a}`,
      scorers: "Marcadores",
      ownGoalShort: "ap",
      penaltyShort: "gp",
      score: (h, a) => `${h}–${a}`,
      none: "Ainda não há jogos disputados.",
    },
    standings: {
      heading: "Classificação",
      team: "Equipa",
      note: "Classificação com base nos jogos já disputados. Apuram-se os dois primeiros de cada grupo e os melhores terceiros.",
      cols: {
        mp: "J",
        w: "V",
        d: "E",
        l: "D",
        gf: "GM",
        ga: "GS",
        gd: "DG",
        pts: "Pts",
      },
      colsFull: {
        mp: "Jogos",
        w: "Vitórias",
        d: "Empates",
        l: "Derrotas",
        gf: "Golos marcados",
        ga: "Golos sofridos",
        gd: "Diferença de golos",
        pts: "Pontos",
      },
    },
    scorers: {
      heading: "Melhores marcadores",
      player: "Jogador",
      goals: "Golos",
    },
    bracket: {
      heading: "Caminho até à final",
      intro:
        "Os jogos a eliminar, dos quartos de final à final — veja os próximos jogos e os resultados. Datas e horas de Portugal.",
    },
  },
  en: {
    map: {
      heading: "Where to watch the 2026 World Cup in Portugal",
      description:
        "Fan zones, bars and restaurants with big screens. Tap a pin for details.",
      venuesHeading: "Venues",
      locate: "Show on map",
      tentative: "To be confirmed",
    },
    calendar: {
      heading: "2026 World Cup fixtures",
      filterDate: "Day",
      filterStage: "Stage",
      filterTeam: "Search team or group",
      onlyPortugal: "Portugal only",
      all: "All",
      tbd: "To be confirmed",
      kickoff: "Portugal time",
      noResults: "No matches for these filters.",
      matchesCount: (n) => `${n} ${n === 1 ? "match" : "matches"}`,
      datesNote:
        "Group-stage dates and times are confirmed (Portugal time). Knockouts are provisional until the bracket is set.",
      provisional: "Provisional date/time",
    },
    planner: {
      heading: "Plan your matches",
      intro:
        "Pick the matches you don't want to miss and where you'll watch them. Your plan is saved on this device.",
      add: "Add to my plan",
      added: "In my plan",
      whereWatch: "Where will you watch?",
      atHome: "At home",
      pick: "Pick a venue",
      myPlan: "My plan",
      planCount: (n) => `${n} ${n === 1 ? "match" : "matches"} planned`,
      exportIcs: "Export to calendar (.ics)",
      clear: "Clear plan",
      empty: "No matches yet. Add the games you want to watch above.",
      savedHint: "Saved automatically in this browser.",
    },
    stages: {
      group: "Group stage",
      r32: "Round of 32",
      r16: "Round of 16",
      qf: "Quarter-finals",
      sf: "Semi-finals",
      third: "Third place",
      final: "Final",
    },
    features: {
      telao: "Big screen",
      "ar-livre": "Outdoors",
      gratis: "Free entry",
      reservas: "Reservations",
    },
    broadcast: {
      paid: "Subscription channel",
      free: "Free-to-air (free)",
      legend:
        "Every game on Sport TV (subscription). Free-to-air: RTP1, SIC or TVI on the marked games, and LiveModeTV (YouTube) for Portugal's games, the semi-finals and the final.",
    },
    result: {
      recentHeading: "Results",
      finalScore: "Final score",
      afterPens: (h, a) => `on penalties ${h}–${a}`,
      scorers: "Scorers",
      ownGoalShort: "og",
      penaltyShort: "pen",
      score: (h, a) => `${h}–${a}`,
      none: "No matches played yet.",
    },
    standings: {
      heading: "Table",
      team: "Team",
      note: "Standings based on matches played so far. The top two of each group plus the best third-placed teams advance.",
      cols: {
        mp: "MP",
        w: "W",
        d: "D",
        l: "L",
        gf: "GF",
        ga: "GA",
        gd: "GD",
        pts: "Pts",
      },
      colsFull: {
        mp: "Matches played",
        w: "Won",
        d: "Drawn",
        l: "Lost",
        gf: "Goals for",
        ga: "Goals against",
        gd: "Goal difference",
        pts: "Points",
      },
    },
    scorers: {
      heading: "Top scorers",
      player: "Player",
      goals: "Goals",
    },
    bracket: {
      heading: "Road to the final",
      intro:
        "The knockout matches, from the quarter-finals to the final — see the upcoming games and the scores. Dates and times are Portugal time.",
    },
  },
  fr: {
    map: {
      heading: "Où regarder la Coupe du Monde 2026 au Portugal",
      description:
        "Fan zones, bars et restaurants avec écran géant. Touchez un point pour les détails.",
      venuesHeading: "Lieux",
      locate: "Voir sur la carte",
      tentative: "À confirmer",
    },
    calendar: {
      heading: "Calendrier de la Coupe du Monde 2026",
      filterDate: "Jour",
      filterStage: "Phase",
      filterTeam: "Rechercher une équipe ou un groupe",
      onlyPortugal: "Portugal seulement",
      all: "Tous",
      tbd: "À confirmer",
      kickoff: "Heure du Portugal",
      noResults: "Aucun match pour ces filtres.",
      matchesCount: (n) => `${n} ${n === 1 ? "match" : "matchs"}`,
      datesNote:
        "Dates et heures de la phase de groupes confirmées (heure du Portugal). Les éliminatoires sont provisoires jusqu'au tirage du tableau.",
      provisional: "Date/heure provisoire",
    },
    planner: {
      heading: "Planifiez vos matchs",
      intro:
        "Choisissez les matchs à ne pas manquer et où les regarder. Votre plan est enregistré sur cet appareil.",
      add: "Ajouter à mon plan",
      added: "Dans mon plan",
      whereWatch: "Où allez-vous regarder ?",
      atHome: "À la maison",
      pick: "Choisir un lieu",
      myPlan: "Mon plan",
      planCount: (n) => `${n} ${n === 1 ? "match prévu" : "matchs prévus"}`,
      exportIcs: "Exporter vers le calendrier (.ics)",
      clear: "Effacer le plan",
      empty:
        "Aucun match pour l'instant. Ajoutez les matchs à regarder ci-dessus.",
      savedHint: "Enregistré automatiquement dans ce navigateur.",
    },
    stages: {
      group: "Phase de groupes",
      r32: "16es de finale",
      r16: "8es de finale",
      qf: "Quarts de finale",
      sf: "Demi-finales",
      third: "Petite finale",
      final: "Finale",
    },
    features: {
      telao: "Écran géant",
      "ar-livre": "En plein air",
      gratis: "Entrée gratuite",
      reservas: "Réservations",
    },
    broadcast: {
      paid: "Chaîne payante",
      free: "En clair (gratuit)",
      legend:
        "Tous les matchs sur Sport TV (payant). En clair et gratuit : RTP1, SIC ou TVI sur les matchs indiqués, et LiveModeTV (YouTube) pour les matchs du Portugal, les demi-finales et la finale.",
    },
    result: {
      recentHeading: "Résultats",
      finalScore: "Score final",
      afterPens: (h, a) => `aux tirs au but ${h}–${a}`,
      scorers: "Buteurs",
      ownGoalShort: "csc",
      penaltyShort: "pén",
      score: (h, a) => `${h}–${a}`,
      none: "Aucun match encore disputé.",
    },
    standings: {
      heading: "Classement",
      team: "Équipe",
      note: "Classement basé sur les matchs déjà joués. Les deux premiers de chaque groupe et les meilleurs troisièmes se qualifient.",
      cols: {
        mp: "J",
        w: "G",
        d: "N",
        l: "P",
        gf: "BP",
        ga: "BC",
        gd: "Diff",
        pts: "Pts",
      },
      colsFull: {
        mp: "Matchs joués",
        w: "Gagnés",
        d: "Nuls",
        l: "Perdus",
        gf: "Buts pour",
        ga: "Buts contre",
        gd: "Différence de buts",
        pts: "Points",
      },
    },
    scorers: {
      heading: "Meilleurs buteurs",
      player: "Joueur",
      goals: "Buts",
    },
    bracket: {
      heading: "En route vers la finale",
      intro:
        "Les matchs à élimination directe, des quarts de finale à la finale — les prochains matchs et les scores. Dates et heures au Portugal.",
    },
  },
};

export function strFor(lang: string | undefined): MundialStrings {
  if (lang === "en" || lang?.startsWith("en-")) return STR.en;
  if (lang === "fr" || lang?.startsWith("fr-")) return STR.fr;
  return STR.pt;
}
